import { useEffect, useMemo, useState } from "react"
import { AlertTriangle, CheckCircle2, ExternalLink, FileCheck2, RefreshCw, Server, type LucideIcon } from "lucide-react"
import {
  evaluateStudioPublishReadiness,
  withServerDatasourceReadinessProjection,
} from "@/services/project-api"
import { useStudioStore } from "@/store/studio-store"
import { PublishGateRows } from "@/shell/ide-v2/components/publish-gate-rows"
import { usePublishReadinessPublish } from "@/features/validation/use-publish-readiness-publish"
import { PublicationReleasePanel } from "@/features/publishing/publication-release-panel"
import { usePublicationRelease } from "@/features/publishing/use-publication-release"
import { publicationVerifiedDeploymentUrl } from "@/features/publishing/publication-release-types"
import {
  type AgenticProductionCanaryEvidence,
  type AgenticProductionCanaryModel,
  type AgenticProductionCanaryTone,
  AGENTIC_CANARY_ENV_NAMES,
  agenticCanaryStorageKey,
  buildAgenticProductionCanaryModel,
  findAgenticProductionCanaryEvidence,
  parseAgenticProductionCanaryEvidence,
} from "./agentic-production-canary"
import {
  buildProductionCanaryProofRecord,
  buildStudioProofRecordsFromProject,
  findLatestProductionReadbackProofRecord,
  getProductionReadbackEvidenceFromProofRecord,
  removeProjectBackedStudioProofRecord,
  saveProjectBackedStudioProofRecord,
  type StudioProofRecord,
} from "./proof-records"
import { AskCogoSurfaceButton } from "../components/ask-cogo-surface-button"
import { useStudioLocale } from "@/i18n"
import {
  getStudioPublishDeployCopy,
  type StudioPublishDeployCopy,
} from "@/i18n/studio-publish-deploy-copy"
import {
  applianceAgentRuntimeBaseUrl,
  countAgentCardSkills,
  createAgentRuntimeClient,
  formatAgentRuntimeRpcLine,
  KG_SPARQL_DEPLOY_PROBE_QUERY,
  resolveAdvertisedAgentRuntime,
  rosterRefusalFromRpcBody,
  skillIdsFromAgentCard,
} from "@/features/chat/services/studio-agent-runtime-a2a"

const EXTERNAL_RELEASE_BOUNDARY_COPY = {
  en: 'External Cogo publication. This URL is not a DGX installation receipt.',
  ko: '외부 Cogo 발행입니다. 이 URL은 DGX 설치 영수증이 아닙니다.',
  ja: '外部 Cogo 公開です。この URL は DGX インストール領収書ではありません。',
  zh: '这是外部 Cogo 发布。此 URL 不是 DGX 安装凭证。',
  'zh-TW': '這是外部 Cogo 發佈。此 URL 不是 DGX 安裝憑證。',
} as const

function isExternalCogoRelease(url: string | null): boolean {
  if (!url) return false
  try {
    const hostname = new URL(url).hostname.toLowerCase()
    return hostname === 'cogo.xyz' || hostname.endsWith('.cogo.xyz')
  } catch {
    return false
  }
}

/**
 * Deploy surface — IDE v2 inner shell for the Deploy rail item.
 */
export function DeploySurface() {
  const { locale } = useStudioLocale()
  const surfaceCopy = getStudioPublishDeployCopy(locale)
  const copy = surfaceCopy.launch
  const [manualCanaryInput, setManualCanaryInput] = useState("")
  const [manualCanaryError, setManualCanaryError] = useState<string | null>(null)
  const [storedCanaryEvidence, setStoredCanaryEvidence] = useState<AgenticProductionCanaryEvidence | null>(null)
  const [proofRefreshing, setProofRefreshing] = useState(false)
  const [proofRefreshError, setProofRefreshError] = useState<string | null>(null)
  const currentProjectId = useStudioStore((s) => s.currentProjectId)
  const currentProject = useStudioStore((s) => s.currentProject)
  const projectMeta = useStudioStore((s) => s.projectMeta)
  const projectPlan = useStudioStore((s) => s.projectPlan)
  const refreshProofRecords = useStudioStore((s) => s.refreshProofRecords)
  const publication = usePublicationRelease(currentProjectId)
  const slug = projectMeta?.slug ?? null
  const accessToken = useStudioStore((s) => s.session?.access_token ?? null)
  const [agentRuntimeCard, setAgentRuntimeCard] = useState<{
    slug: string
    status: number
    skillsCount: number
    card: unknown
    applicationId?: string
  } | null>(null)
  const [agentRuntimeRpc, setAgentRuntimeRpc] = useState<{
    slug: string
    status: number
    unauthorized: boolean
    rosterRefusal?: string
  } | null>(null)
  const target = projectMeta?.app_target ?? "web"
  const status = projectMeta?.status ?? "draft"
  const version = projectMeta?.version ?? 0
  const liveUrl = publicationVerifiedDeploymentUrl(publication.release) ?? null
  const externalCogoRelease = isExternalCogoRelease(liveUrl)
  const readinessProject = withServerDatasourceReadinessProjection(
    currentProject,
    projectMeta,
    currentProjectId,
  )
  const publishGate = evaluateStudioPublishReadiness(
    readinessProject as never,
    projectPlan ?? projectMeta?.studioProjectPlan ?? null,
    currentProjectId,
  )
  const canaryStorageKey = agenticCanaryStorageKey(currentProjectId)
  const embeddedCanaryEvidence = findAgenticProductionCanaryEvidence(projectMeta, currentProject)
  const proofRecords = useMemo(
    () => buildStudioProofRecordsFromProject(currentProjectId, currentProject, projectMeta),
    [currentProjectId, currentProject, projectMeta],
  )
  const latestProductionReadbackProof = findLatestProductionReadbackProofRecord(proofRecords)
  const proofRecordCanaryEvidence = getProductionReadbackEvidenceFromProofRecord(latestProductionReadbackProof)
  const activeCanaryEvidence = storedCanaryEvidence ?? proofRecordCanaryEvidence ?? embeddedCanaryEvidence
  const canaryModel = buildAgenticProductionCanaryModel(activeCanaryEvidence)
  const canaryEvidenceSource: keyof StudioPublishDeployCopy["canary"]["evidenceSources"] =
    storedCanaryEvidence
      ? "manualImport"
      : proofRecordCanaryEvidence
        ? "proofRecord"
        : embeddedCanaryEvidence
          ? "projectMetadata"
          : "none"
  const readbackProofBlocksPublish = latestProductionReadbackProof?.status === "blocked"

  // The Deploy rail is also a real publishing entry point — publish the same
  // readiness pre-flight estimate the Publish rail does, so the app-health pill
  // gets a readiness vote on this path too (ST-5).
  usePublishReadinessPublish(currentProjectId, publishGate, locale)

  useEffect(() => {
    setAgentRuntimeCard(null)
    setAgentRuntimeRpc(null)
    if (!slug) {
      return
    }
    if (typeof window === "undefined") return

    let cancelled = false
    void resolveAdvertisedAgentRuntime(window.location.origin, slug)
      .then((resolved) => {
        if (cancelled) return
        if (!resolved) {
          setAgentRuntimeCard({
            slug,
            status: 0,
            skillsCount: 0,
            card: null,
          })
          return
        }
        setAgentRuntimeCard({
          slug,
          status: resolved.status,
          skillsCount: countAgentCardSkills(resolved.card),
          card: resolved.card,
          applicationId: resolved.applicationId,
        })
      })
      .catch(() => {
        if (cancelled) return
        setAgentRuntimeCard({
          slug,
          status: 0,
          skillsCount: 0,
          card: null,
        })
        setAgentRuntimeRpc(null)
      })

    return () => {
      cancelled = true
    }
  }, [slug])

  useEffect(() => {
    if (!slug || !accessToken) {
      setAgentRuntimeRpc(null)
      return
    }
    if (!agentRuntimeCard || agentRuntimeCard.slug !== slug) {
      setAgentRuntimeRpc(null)
      return
    }
    if (typeof window === "undefined") return

    let cancelled = false
    const applicationId = agentRuntimeCard.applicationId ?? slug
    try {
      applianceAgentRuntimeBaseUrl(window.location.origin, applicationId)
    } catch {
      setAgentRuntimeRpc(null)
      return
    }

    const client = createAgentRuntimeClient({
      origin: window.location.origin,
      applicationId,
      accessToken,
    })
    const skillIds = skillIdsFromAgentCard(agentRuntimeCard.card)
    const kgSparql = skillIds.includes("kg-sparql") ? "kg-sparql" : undefined
    void client
      .sendMessage({
        messageId: crypto.randomUUID(),
        text: "probe",
        ...(kgSparql
          ? { skillId: kgSparql, params: { query: KG_SPARQL_DEPLOY_PROBE_QUERY } }
          : {}),
      })
      .then((result) => {
        if (cancelled) return
        setAgentRuntimeRpc({
          slug,
          status: result.status,
          unauthorized: result.unauthorized || result.status === 401,
          rosterRefusal: rosterRefusalFromRpcBody(result.body),
        })
      })
      .catch(() => {
        if (cancelled) return
        setAgentRuntimeRpc(null)
      })

    return () => {
      cancelled = true
    }
  }, [slug, accessToken, agentRuntimeCard])

  useEffect(() => {
    setManualCanaryError(null)
    setStoredCanaryEvidence(null)
    if (typeof window === "undefined") return

    const raw = window.localStorage.getItem(canaryStorageKey)
    if (!raw) return

    const parsed = parseAgenticProductionCanaryEvidence(raw)
    if (parsed.ok) {
      setStoredCanaryEvidence(parsed.evidence)
    } else {
      window.localStorage.removeItem(canaryStorageKey)
    }
  }, [canaryStorageKey])

  function handleCanaryEvidenceImport() {
    const parsed = parseAgenticProductionCanaryEvidence(manualCanaryInput)
    if (!parsed.ok) {
      setManualCanaryError(parsed.error)
      return
    }

    const importedModel = buildAgenticProductionCanaryModel(parsed.evidence)
    const proofRecord = buildProductionCanaryProofRecord({
      projectId: currentProjectId,
      evidence: parsed.evidence,
      model: importedModel,
    })

    setManualCanaryError(null)
    setStoredCanaryEvidence(parsed.evidence)
    setManualCanaryInput("")
    if (typeof window !== "undefined") {
      window.localStorage.setItem(canaryStorageKey, JSON.stringify(parsed.evidence))
    }
    void saveProjectBackedStudioProofRecord(currentProjectId, proofRecord).catch((error) => {
      console.warn("[DeploySurface] proof record server save failed", error)
      setManualCanaryError(surfaceCopy.canary.savedLocallySyncFailed)
    })
  }

  function handleCanaryEvidenceClear() {
    if (storedCanaryEvidence) {
      const record = buildProductionCanaryProofRecord({
        projectId: currentProjectId,
        evidence: storedCanaryEvidence,
        model: buildAgenticProductionCanaryModel(storedCanaryEvidence),
      })
      void removeProjectBackedStudioProofRecord(currentProjectId, record.id).catch((error) => {
        console.warn("[DeploySurface] proof record server remove failed", error)
        setManualCanaryError(surfaceCopy.canary.clearedLocallySyncFailed)
      })
    }
    setManualCanaryError(null)
    setStoredCanaryEvidence(null)
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(canaryStorageKey)
    }
  }

  async function handleProofRecordsRefresh() {
    if (proofRefreshing) return
    setProofRefreshing(true)
    setProofRefreshError(null)
    try {
      const refreshed = await refreshProofRecords()
      if (!refreshed) {
        setProofRefreshError(copy.refreshNeedsSavedProject)
      }
    } catch (error) {
      console.warn("[DeploySurface] proof record refresh failed", error)
      setProofRefreshError(copy.refreshFailed)
    } finally {
      setProofRefreshing(false)
    }
  }

  return (
    <div className="h-full overflow-auto p-4 sm:p-6 lg:p-8" data-testid="deploy-surface">
      <div className="mx-auto max-w-4xl">
        <header className="mb-6">
          <div className="mb-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            {copy.eyebrow}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">{copy.heading}</h1>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground">
            {copy.intro}
          </p>
        </header>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <AskCogoSurfaceButton
            label={copy.askLabel}
            context={() => [
              'Studio launch surface: explain authorization, deployment receipt, artifact readback, live-server readback, retry, and rollback blockers.',
              `Project=${currentProjectId ?? 'missing'}`,
              `Target=${publication.release?.targetProfile.profileId ?? target}`,
              `Release state=${publication.release?.derivedState ?? 'none'}`,
              `Verified URL=${liveUrl ?? 'none'}`,
              `Studio service canary=${canaryModel.status}`,
              readbackProofBlocksPublish ? 'Legacy Studio service readback is blocked' : null,
            ].filter(Boolean).join('; ')}
          />
        </div>

        <PublicationReleasePanel
          mode="launch"
          controller={publication}
          projectId={currentProjectId}
          slug={slug}
        />

        <details className="mt-4 rounded-lg border border-border bg-card">
          <summary className="flex min-h-12 cursor-pointer items-center justify-between px-5 py-3 text-xs font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <span>{copy.diagnosticsSummary}</span>
            <span className="font-mono text-[10px] text-muted-foreground">{copy.diagnosticsBadge}</span>
          </summary>
          <div className="border-t border-border">
            <Row testId="legacy-status" label={copy.legacyStatus}><span className="font-mono text-xs text-muted-foreground">{status}</span></Row>
            <Row testId="slug" label={copy.slug}><span className="font-mono text-xs text-foreground">{slug ?? '—'}</span></Row>
            <Row testId="agent-runtime-card" label={copy.agentRuntimeCard}>
              <span className="font-mono text-xs text-muted-foreground">
                {!slug
                  ? "—"
                  : [
                      agentRuntimeCard && agentRuntimeCard.slug === slug
                        ? `HTTP ${agentRuntimeCard.status} · skills ${agentRuntimeCard.skillsCount}`
                        : "—",
                      agentRuntimeRpc && agentRuntimeRpc.slug === slug
                        ? formatAgentRuntimeRpcLine(agentRuntimeRpc)
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
              </span>
            </Row>
            <Row testId="target" label={copy.target}><span className="font-mono text-xs text-foreground">{target}</span></Row>
            <Row testId="saved-version" label={copy.savedVersion}><span className="font-mono text-xs text-foreground">v{version}</span></Row>
            <Row testId="verified-release-url" label={copy.verifiedReleaseUrl}>
              {liveUrl ? (
                <span className="flex min-w-0 flex-col items-end gap-1">
                  <a href={liveUrl} target="_blank" rel="noopener noreferrer" className="max-w-full truncate font-mono text-xs text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    {liveUrl}
                  </a>
                  {externalCogoRelease ? (
                    <span
                      data-testid="external-cogo-release-boundary"
                      className="max-w-lg text-right text-[11px] leading-4 text-muted-foreground"
                    >
                      {EXTERNAL_RELEASE_BOUNDARY_COPY[locale]}
                    </span>
                  ) : null}
                </span>
              ) : <span className="text-xs text-muted-foreground">{copy.noVerifiedReceipt}</span>}
            </Row>
            <ProductionReadbackProofRow
              copy={copy}
              record={latestProductionReadbackProof}
              refreshing={proofRefreshing}
              refreshError={proofRefreshError}
              onRefresh={handleProofRecordsRefresh}
            />
            <PublishGateRows publishGate={publishGate} />
          </div>
        </details>

        <details
          className="mt-4 rounded-lg border border-border bg-card"
          data-testid="studio-service-health-details"
        >
          <summary className="cursor-pointer list-none px-5 py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <h2 id="studio-service-health-title" className="text-sm font-semibold text-foreground">{copy.serviceHealthHeading}</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {copy.serviceHealthDetail}
            </p>
          </summary>
          <div className="border-t border-border p-4" aria-labelledby="studio-service-health-title">
            <AgenticProductionCanaryCard
              copy={surfaceCopy.canary}
              model={canaryModel}
              evidenceSource={canaryEvidenceSource}
              manualInput={manualCanaryInput}
              manualError={manualCanaryError}
              hasStoredEvidence={Boolean(storedCanaryEvidence)}
              onManualInputChange={setManualCanaryInput}
              onImport={handleCanaryEvidenceImport}
              onClear={handleCanaryEvidenceClear}
            />
          </div>
        </details>
      </div>
    </div>
  )
}

function ProductionReadbackProofRow({
  copy,
  record,
  refreshing,
  refreshError,
  onRefresh,
}: {
  copy: StudioPublishDeployCopy["launch"]
  record: StudioProofRecord | null
  refreshing: boolean
  refreshError: string | null
  onRefresh: () => void
}) {
  if (!record) {
    return (
      <Row testId="live-check" label={copy.liveCheck}>
        <div className="max-w-[360px] text-right">
          <span className="text-xs text-muted-foreground">{copy.noLiveCheckYet}</span>
          <ProofRefreshButton copy={copy} refreshing={refreshing} onRefresh={onRefresh} />
          {refreshError && <div className="mt-1 text-[11px] text-destructive">{refreshError}</div>}
        </div>
      </Row>
    )
  }

  return (
    <Row testId="live-check" label={copy.liveCheck}>
      <div className="max-w-[360px] text-right">
        <span className={`font-mono text-xs ${proofRecordStatusClass(record.status)}`}>
          {record.status}
        </span>
        <div className="mt-1 truncate text-[11px] text-muted-foreground">
          {record.title} · {formatDeployProofDate(record.createdAt)}
        </div>
        {(record.missing.length > 0 || record.failureReasons.length > 0) && (
          <div className="mt-1 text-[11px] text-destructive">
            {[...record.missing, ...record.failureReasons][0]}
          </div>
        )}
        <ProofRefreshButton copy={copy} refreshing={refreshing} onRefresh={onRefresh} />
        {refreshError && <div className="mt-1 text-[11px] text-destructive">{refreshError}</div>}
      </div>
    </Row>
  )
}

function ProofRefreshButton({
  copy,
  refreshing,
  onRefresh,
}: {
  copy: StudioPublishDeployCopy["launch"]
  refreshing: boolean
  onRefresh: () => void
}) {
  return (
    <button
      type="button"
      onClick={onRefresh}
      disabled={refreshing}
      className="mt-2 inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-background px-2 text-[11px] font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
    >
      <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} aria-hidden />
      {refreshing ? copy.refreshing : copy.refreshProof}
    </button>
  )
}

function AgenticProductionCanaryCard({
  copy,
  model,
  evidenceSource,
  manualInput,
  manualError,
  hasStoredEvidence,
  onManualInputChange,
  onImport,
  onClear,
}: {
  copy: StudioPublishDeployCopy["canary"]
  model: AgenticProductionCanaryModel
  evidenceSource: keyof StudioPublishDeployCopy["canary"]["evidenceSources"]
  manualInput: string
  manualError: string | null
  hasStoredEvidence: boolean
  onManualInputChange: (value: string) => void
  onImport: () => void
  onClear: () => void
}) {
  const preflightItems = copy.beforeProductionItems
  const readbackItems = copy.requiredChecksItems
  const failClosedItems = copy.failClosedItems

  return (
    <section
      className="mt-4 rounded-lg border border-border bg-card p-5"
      data-testid="agentic-production-canary-card"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="mb-2 flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground">
              <Server className="h-4 w-4" aria-hidden />
            </span>
            <div>
              <h2 className="text-sm font-semibold text-foreground">{copy.heading}</h2>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {model.summary}
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <StatusBadge tone="success">{copy.localProofReady}</StatusBadge>
          <StatusBadge tone={model.tone}>{model.label}</StatusBadge>
          <StatusBadge tone="muted">
            {copy.evidenceLabel(copy.evidenceSources[evidenceSource])}
          </StatusBadge>
        </div>
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-3">
        <CanaryChecklist
          icon={CheckCircle2}
          title={copy.beforeProductionHeading}
          items={preflightItems}
        />
        <CanaryChecklist
          icon={FileCheck2}
          title={copy.requiredChecksHeading}
          items={readbackItems}
        />
        <CanaryChecklist icon={AlertTriangle} title={copy.failClosedHeading} items={failClosedItems} />
      </div>

      <div className="mt-4 grid gap-2 rounded-md border border-border bg-background p-3 text-xs lg:grid-cols-[180px_minmax(0,1fr)]">
        <span className="font-medium text-muted-foreground">{copy.envNamesLabel}</span>
        <span className="font-mono text-foreground">
          {AGENTIC_CANARY_ENV_NAMES.join(", ")}
        </span>
        <span className="font-medium text-muted-foreground">{copy.provenanceLabel}</span>
        <span className="font-mono text-foreground">source: production-studio-readback</span>
        <span className="font-medium text-muted-foreground">{copy.checkOutputLabel}</span>
        <span className="font-mono text-foreground">output/agentic-rag-final-proof/evidence.json</span>
      </div>

      <div className="mt-4 grid gap-2 rounded-md border border-border bg-background p-3 text-xs lg:grid-cols-[180px_minmax(0,1fr)]">
        {model.facts.map((fact) => (
          <FactRow key={fact.label} label={fact.label} value={fact.value} missing={fact.missing} />
        ))}
      </div>

      {(model.missing.length > 0 || model.failureReasons.length > 0) && (
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {model.missing.length > 0 && (
            <CanaryChecklist icon={FileCheck2} title={copy.missingChecksHeading} items={model.missing} />
          )}
          {model.failureReasons.length > 0 && (
            <CanaryChecklist icon={AlertTriangle} title={copy.failClosedReasonsHeading} items={model.failureReasons} />
          )}
        </div>
      )}

      <div className="mt-4 rounded-md border border-border bg-background p-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
          <label htmlFor="agentic-canary-evidence-input" className="min-w-[180px] text-xs font-medium text-muted-foreground">
            {copy.jsonLabel}
          </label>
          <div className="min-w-0 flex-1">
            <textarea
              id="agentic-canary-evidence-input"
              value={manualInput}
              onChange={(event) => onManualInputChange(event.target.value)}
              placeholder={copy.jsonPlaceholder}
              className="h-24 w-full resize-y rounded-md border border-input bg-card px-3 py-2 font-mono text-xs text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/20"
            />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={onImport}
                className="inline-flex h-8 items-center rounded-md border border-border bg-foreground px-3 text-xs font-medium text-background transition-colors hover:bg-foreground/90"
              >
                {copy.importButton}
              </button>
              <button
                type="button"
                onClick={onClear}
                disabled={!hasStoredEvidence}
                className="inline-flex h-8 items-center rounded-md border border-border bg-card px-3 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
              >
                {copy.clearButton}
              </button>
              <a
                href="/studio/library#proof-records"
                className="inline-flex h-8 items-center gap-2 rounded-md border border-border bg-card px-3 text-xs font-medium text-foreground transition-colors hover:bg-accent"
              >
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                {copy.libraryHub}
              </a>
            </div>
            {manualError ? (
              <p className="mt-2 text-xs text-destructive">{manualError}</p>
            ) : (
              <p className="mt-2 text-xs text-muted-foreground">
                {copy.diagnosticNote}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

function proofRecordStatusClass(status: StudioProofRecord["status"]): string {
  if (status === "pass") return "text-foreground"
  if (status === "review") return "text-muted-foreground"
  return "text-destructive"
}

function formatDeployProofDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

function FactRow({ label, value, missing }: { label: string; value: string; missing?: boolean }) {
  return (
    <>
      <span className="font-medium text-muted-foreground">{label}</span>
      <span className={missing ? "font-mono text-muted-foreground" : "font-mono text-foreground"}>{value}</span>
    </>
  )
}

function CanaryChecklist({
  icon: Icon,
  title,
  items,
}: {
  icon: LucideIcon
  title: string
  items: readonly string[]
}) {
  return (
    <div className="rounded-md bg-background p-3">
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
        <h3 className="text-xs font-semibold text-foreground">{title}</h3>
      </div>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li key={item} className="flex gap-2 text-xs text-muted-foreground">
            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/60" aria-hidden />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function StatusBadge({ tone, children }: { tone: AgenticProductionCanaryTone; children: React.ReactNode }) {
  const className = tone === "success"
    ? "border-border bg-muted text-foreground"
    : tone === "destructive"
      ? "border-destructive/25 bg-destructive/10 text-destructive"
      : tone === "warning"
        ? "border-border bg-muted text-muted-foreground"
        : "border-border bg-muted text-muted-foreground"

  return (
    <span className={`inline-flex h-7 items-center rounded-full border px-2.5 text-[11px] font-medium ${className}`}>
      {children}
    </span>
  )
}

/**
 * `testId` is an explicit, locale-INDEPENDENT key. It used to be derived from
 * `label`, which meant localizing the label silently renamed every
 * `deploy-gate-*` testid its consumers resolve by
 * (`__tests__/deploy-surface.test.tsx`, `e2e/studio/sipass-admin-publish-deploy-settings.spec.ts`).
 */
function Row({ testId, label, children }: { testId: string; label: string; children: React.ReactNode }) {
  const dataTestId = `deploy-gate-${testId}`

  return (
    <div data-testid={dataTestId} className="flex items-center justify-between border-b border-border px-5 py-3 last:border-0">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div>{children}</div>
    </div>
  )
}
